import PropTypes from 'prop-types';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Link from '@zenbusiness/zui/Link';
import SkeletonGrid from '@zenbusiness/zui/SkeletonGrid';
import Stack from '@zenbusiness/zui/Stack';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { ENABLE_BLR_SPECIFIC_CONTENT } from '#client/constants/experimentsFlags';
import { filterProductCardsByFeatureFlags, useProductCardFeatureFlags } from '#client/utils/productCardFlags';

import * as Styled from './ProductCards.style';

/**
 * TODO: Remove this override behavior once enable-blr-specific-content flag is permanently enabled.
 *
 * This is a temporary, bespoke solution to decouple card content from the service product.
 * It allows us to gate the new BLR-specific content (title/description) while maintaining
 * backward compatibility with the generic "Business License" content when the flag is disabled.
 *
 * Once the flag is enabled for all users and we're confident in the new content, we should:
 * 1. Remove PRODUCT_TITLE_OVERRIDES and PRODUCT_DESCRIPTION_OVERRIDES
 * 2. Remove the conditional override logic in the map function
 * 3. Remove the legacy translation keys from messages.json
 * 4. Rely solely on the titleKey/descriptionKey from the backend service
 */

// Business License product card title override —
// When feature flag is disabled, use generic "Business License" title instead of BLR-specific content
const PRODUCT_TITLE_OVERRIDES = {
  'product.name.business_license_report': 'product.name.business_license_report.overrides',
};

// Business License product card description override —
// When feature flag is disabled, use generic description instead of BLR-specific content
const PRODUCT_DESCRIPTION_OVERRIDES = {
  'dashboard.products.business_license_report.description':
    'dashboard.products.business_license_report.description.legacy',
};

// Gusto product card destination override — points the card at the new internal
// Gusto landing page (src/pages/ProductLanding/constants/gusto.js) instead of
// whatever primaryPath/primaryExternal the backend currently returns for it.
const PRODUCT_PRIMARY_PATH_OVERRIDES = {
  'product.name.gusto': '/dashboard/{BUUID}/products/gusto',
};

function ProductCards({ items, loading, elementPosition = 'products_page', columns = 3 }) {
  const { formatMessage: fm } = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const { businessEntityUuid } = useBusinessEntity();
  const featureFlagValues = useProductCardFeatureFlags();
  const enableBlrSpecificContent = featureFlagValues[ENABLE_BLR_SPECIFIC_CONTENT];

  const visibleItems = filterProductCardsByFeatureFlags(items, featureFlagValues);

  if (loading) {
    return <SkeletonGrid columns={isDesktop ? 3 : 2} count={isDesktop ? 9 : 6} />;
  }

  if (visibleItems.length === 0) {
    return null;
  }
  return (
    <Styled.ListCard columns={columns}>
      {visibleItems.map(({ titleKey, descriptionKey, imagePath, primaryExternal, primaryPath }) => {
        // TODO: Remove this conditional override logic once enable-blr-specific-content flag is permanent
        // Temporary override: Use generic content when flag is disabled, otherwise use BLR-specific content from backend
        const title = fm({
          id:
            !enableBlrSpecificContent && PRODUCT_TITLE_OVERRIDES[titleKey]
              ? PRODUCT_TITLE_OVERRIDES[titleKey]
              : titleKey,
        });
        const description = fm({
          id:
            !enableBlrSpecificContent && PRODUCT_DESCRIPTION_OVERRIDES[descriptionKey]
              ? PRODUCT_DESCRIPTION_OVERRIDES[descriptionKey]
              : descriptionKey,
        });
        const primaryPathOverride = PRODUCT_PRIMARY_PATH_OVERRIDES[titleKey];
        const resolvedPrimaryPath = primaryPathOverride || primaryPath;
        const resolvedPrimaryExternal = primaryPathOverride ? undefined : primaryExternal;

        return (
          <Styled.ProductListItem key={titleKey}>
            <Styled.ProductCard
              aria-label={title}
              clickstreamProps={{
                elementName: 'product_card',
                elementPosition,
                elementValue: title,
                destination: resolvedPrimaryPath?.replace('{BUUID}', businessEntityUuid) || resolvedPrimaryExternal,
              }}
              component={Link}
              data-testid="product-card-list-item"
              title={title}
              size="xl"
              {...(resolvedPrimaryExternal && { target: '_blank', href: resolvedPrimaryExternal })}
              {...(resolvedPrimaryPath && { to: resolvedPrimaryPath.replace('{BUUID}', businessEntityUuid) })}
              variant="elevation"
            >
              <Stack alignItems="left" spacing={2}>
                <Styled.CardImageContainer>
                  <Styled.CardImage alt="" src={imagePath} />
                </Styled.CardImageContainer>
                {resolvedPrimaryExternal && (
                  <Styled.ExternalIconContainer>
                    <OpenInNewIcon aria-hidden="true" />
                  </Styled.ExternalIconContainer>
                )}
                <Stack gap={0.5}>
                  <Text size="sm" color="primary" weight="medium">
                    {title}
                  </Text>
                  <Text size="xs" color="secondary" weight="regular">
                    {description}
                  </Text>
                </Stack>
              </Stack>
            </Styled.ProductCard>
          </Styled.ProductListItem>
        );
      })}
    </Styled.ListCard>
  );
}

ProductCards.propTypes = {
  loading: PropTypes.bool,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      titleKey: PropTypes.string,
      descriptionKey: PropTypes.string,
      imagePath: PropTypes.string,
      primaryExternal: PropTypes.string,
      primaryPath: PropTypes.string,
    }),
  ).isRequired,
  elementPosition: PropTypes.string,
  columns: PropTypes.number,
};

export default ProductCards;
